import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AdminNavigation from "./AdminNavigation";

function UpdateTranslatorsDataByAdmin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    id: "",
    fname: "",
    lname: "",
    email: "",
    username: "",
    dob: "",
    gender: "",
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const token = localStorage.getItem("adminToken");

  useEffect(() => {
    const id = localStorage.getItem("ID");
    if (!id) {
      setErrors({ form: "No translator ID found. Please select a translator to edit." });
      setFetching(false);
      return;
    }

    const fetchTranslatorData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/users/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const { fname, lname, email, username, dob, gender } = response.data.data;
        setFormData({
          id,
          fname: fname || "",
          lname: lname || "",
          email: email || "",
          username: username || "",
          dob: dob ? dob.split("T")[0] : "", // Format date for input
          gender: gender || "",
        });
      } catch (error) {
        console.error("Error fetching translator data:", error);
        if (error.response?.status === 401) {
          setErrors({ form: "Unauthorized. Please log in again." });
          navigate("/admin/login");
        } else {
          setErrors({ form: "Failed to load translator data. Please try again." });
        }
      } finally {
        setFetching(false);
      }
    };

    if (token) {
      fetchTranslatorData();
    } else {
      setErrors({ form: "Authentication token is missing. Please log in again." });
      navigate("/admin/login");
      setFetching(false);
    }
  }, [navigate, token]);

  const validateForm = () => {
    const errors = {};
    const { fname, lname, email, username, dob, gender } = formData;

    if (!fname.trim()) errors.fname = "First name is required.";
    if (!lname.trim()) errors.lname = "Last name is required.";
    if (!email) errors.email = "Email address is required.";
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = "Invalid email address.";
    if (!username) errors.username = "Username is required.";
    if (!dob) errors.dob = "Date of birth is required.";
    else {
      const today = new Date();
      const birthDate = new Date(dob);
      if (birthDate > today) errors.dob = "Date of birth cannot be in the future.";
      else if (today.getFullYear() - birthDate.getFullYear() < 18)
        errors.dob = "Translator must be at least 18 years old.";
    }
    if (!gender) errors.gender = "Gender is required.";

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage("");

    if (!token) {
      setErrors({ form: "Authentication token is missing. Please log in again." });
      navigate("/admin/login");
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update/${formData.id}`,
        {
          fname: formData.fname,
          lname: formData.lname,
          email: formData.email,
          username: formData.username,
          dob: formData.dob,
          gender: formData.gender,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 0) {
        if (response.data.result.includes("Email id already exist")) {
          setErrors({ email: "Email already exists. Please use a different email address." });
        } else if (response.data.result.includes("Username already exist")) {
          setErrors({ username: "Username already exists. Please choose a different username." });
        } else {
          setErrors({ form: "Update failed. Please try again." });
        }
        return;
      }

      setSuccessMessage("Translator updated successfully! Redirecting...");
      setTimeout(() => navigate("/admin/alltranslators"), 2000);
    } catch (error) {
      console.error("Error updating translator data:", error);
      if (error.response?.status === 401) {
        setErrors({ form: "Unauthorized. Please log in again." });
        navigate("/admin/login");
      } else {
        setErrors({ form: "Failed to update translator data. Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (fetching) {
    return (
      <div className="loading-overlay">
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Loading...
      </div>
    );
  }

  return (
    <div id="wrapper" className="toggled">
      <AdminNavigation />
      <div id="page-content-wrapper">
        <div className="container">
          <h4>Update Translator</h4>
          {successMessage && (
            <div className="alert alert-success mt-3" role="alert">
              {successMessage}
            </div>
          )}
          {errors.form && (
            <div className="alert alert-danger mt-3" role="alert">
              {errors.form}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="mb-3 mt-3">
              <label htmlFor="fname" className="form-label">
                First Name<span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="fname"
                placeholder="Enter first name"
                name="fname"
                value={formData.fname}
                onChange={handleInputChange}
                required
              />
              {errors.fname && <p className="text-danger">{errors.fname}</p>}
            </div>
            <div className="mb-3">
              <label htmlFor="lname" className="form-label">
                Last Name<span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="lname"
                placeholder="Enter last name"
                name="lname"
                value={formData.lname}
                onChange={handleInputChange}
                required
              />
              {errors.lname && <p className="text-danger">{errors.lname}</p>}
            </div>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email<span className="required">*</span>
              </label>
              <input
                type="email"
                className="form-control"
                id="email"
                placeholder="Enter email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              {errors.email && <p className="text-danger">{errors.email}</p>}
            </div>
            <div className="mb-3">
              <label htmlFor="gender" className="form-label">
                Gender<span className="required">*</span>
              </label>
              <select
                className="form-control"
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && <p className="text-danger">{errors.gender}</p>}
            </div>
            <div className="mb-3">
              <label htmlFor="username" className="form-label">
                Username<span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="username"
                placeholder="Enter username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
              {errors.username && <p className="text-danger">{errors.username}</p>}
            </div>
            <div className="mb-3">
              <label htmlFor="dob" className="form-label">
                Date of Birth<span className="required">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                id="dob"
                name="dob"
                value={formData.dob}
                onChange={handleInputChange}
                max={new Date().toISOString().split("T")[0]}
                required
              />
              {errors.dob && <p className="text-danger">{errors.dob}</p>}
            </div>
            <button type="submit" className="btn btn-primary regsiter_btn" disabled={loading || fetching}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default UpdateTranslatorsDataByAdmin;